import { useEffect, useRef, useState } from "react";
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/picasso-firebase";
import * as firebaseui from "firebaseui";
import "firebaseui/dist/firebaseui.css";

interface AuthResponse {
  success: boolean;
  message?: string;
  redirect_url?: string;
  user?: {
    id: string;
    email: string;
    role: string;
    name: string;
  };
}

export default function AuthForm() {
  const uiRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSignInWithBackend = async (idToken: string, email: string, user_id: string, name: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/picasso/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email,
          userId: user_id,  // Backend expects userId (with alias to user_id)
          name
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AuthResponse = await response.json();

      if (data.success) {
        if (data.redirect_url) {
          navigate(data.redirect_url);
        } else {
          navigate('/dashboard');
        }
      } else if (data.message?.includes('pending')) {
        globalThis.alert(data.message);
        await auth.signOut();
      } else {
        setError(data.message || 'Authentication failed');
      }
    } catch (error) {
      console.error('Error during sign-in:', error);
      setError('An error occurred during sign-in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle token retrieval and backend authentication
   */
  const handleTokenAndAuth = async (user: any) => {
    try {
      const email = user.email;
      const user_id = user.uid;
      const name = user.displayName || email.split('@')[0];

      const idToken = await user.getIdToken();
      handleSignInWithBackend(idToken, email, user_id, name);
    } catch (error) {
      console.error('Error getting ID token:', error);
      setError('Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!uiRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      try {
        let ui = firebaseui.auth.AuthUI.getInstance();
        if (ui) {
          ui.delete();
        }
        
        ui = new firebaseui.auth.AuthUI(auth);

        const uiConfig = {
          signInOptions: [
            {
              provider: 'google.com',
              scopes: ['email', 'profile']
            }
          ],
          signInFlow: 'popup',
          credentialHelper: firebaseui.auth.CredentialHelper.NONE,
          tosUrl: '#',
          privacyPolicyUrl: '#',
          callbacks: {
            signInSuccessWithAuthResult: (authResult: any) => {
              setLoading(true);
              setError(null);
              
              handleTokenAndAuth(authResult.user);
              return false;
            },
          signInFailure: (error: any) => {
            setLoading(false);
            
            // Show user-friendly error message
            if (error.code === 'firebaseui/anonymous-upgrade-merge-conflict') {
              setError('Account conflict. Please try again.');
            } else if (error.code === 'auth/popup-blocked') {
              setError('Popup was blocked. Please allow popups and try again.');
            } else if (error.code === 'auth/popup-closed-by-user') {
              setError(null);
            } else {
              setError('Sign in failed. Please try again.');
            }
            
            return Promise.resolve();
          }
          }
        };

        ui.start(uiRef.current!, uiConfig);        
        
    } catch (error) {
      console.error('Error initializing FirebaseUI:', error);
    }
    }, 100);

    return () => {
      clearTimeout(timer);
      try {
        const ui = firebaseui.auth.AuthUI.getInstance();
        if (ui) {
          ui.delete();
        }
      } catch (error) {
        console.error('Error cleaning up FirebaseUI:', error);
      }
    };
  }, []);

  return (
    <Box>
      {loading && (
        <Box sx={{ 
          textAlign: 'center', 
          p: 2.5,
          bgcolor: 'grey.100',
          borderRadius: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Signing you in...</Typography>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box ref={uiRef} sx={{ height: 130 }} />
    </Box>
  );
}